import React from 'react'
import {useIntl} from 'react-intl'
import {ContentHotel} from '@findhotel/sapi'
import {PriceWatchCTAWrapper} from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {PriceWatchToggle} from '@vio/shared/src/priceWatch/components/PriceWatchToggle/PriceWatchToggle'

import {
  DetailedPrice,
  PriceTrendsHistoryResponse
} from '../../services/priceInsightsApi'
import {PriceTrendContent, PriceTrendsTrackingContext} from '../../types'
import {priceTrendMessages} from '../../utils/messages'
import {BetterValueCarousel} from '../BetterValueCarousel'
import {BetterValueHotel} from '../BetterValueCarousel/types'
import {PriceHistoryChart} from '../PriceHistoryChart'
import {getBannerConfig, getBannerState} from '../PriceInsightsBanner'
import {PriceRangeBar, PriceRangeBarSkeleton} from '../PriceRangeBar'
import {PriceTrendHotelCard} from '../PriceTrendBottomSheet/components/PriceTrendHotelCard'
import {PriceTrendInfoCard} from '../PriceTrendInfoCard'

export interface PriceTrendDetailContentProps {
  hotel: ContentHotel
  checkIn: string
  checkOut: string
  content: PriceTrendContent
  recommendationLabel: string
  offerPrice: number | null
  priceHistory: PriceTrendsHistoryResponse | undefined
  isPriceHistoryLoading: boolean
  betterValueHotels: BetterValueHotel[]
  isBetterValueLoading: boolean
  isHigherThanSimilar: boolean
  locationName: string
  priceTrendsContext?: PriceTrendsTrackingContext
  showPriceRangeBar?: boolean
  /** Wording only. Set by the mobile bottom sheet, not the desktop side panel. */
  hasAlertText?: boolean

  latestDetailedPrice?: DetailedPrice | null
  /** Currency for PriceRangeBar formatting. */
  currency?: string
  onTooltipClick?: (infoCardName: string) => void
  onCarouselDisplayed?: (hotels: BetterValueHotel[]) => void
  onCarouselScrolled?: () => void
  onCarouselHotelClicked?: (hotel: BetterValueHotel, position: number) => void
  onCarouselSeeMoreClicked?: () => void
}

export const PriceTrendDetailContent = ({
  hotel,
  checkIn,
  checkOut,
  content,
  recommendationLabel,
  offerPrice,
  priceHistory,
  isPriceHistoryLoading,
  betterValueHotels,
  isBetterValueLoading,
  isHigherThanSimilar,
  locationName,
  priceTrendsContext,
  showPriceRangeBar = false,
  hasAlertText = false,
  latestDetailedPrice = null,
  currency,
  onTooltipClick,
  onCarouselDisplayed,
  onCarouselScrolled,
  onCarouselHotelClicked,
  onCarouselSeeMoreClicked
}: PriceTrendDetailContentProps) => {
  const {retention} = useBrandCapabilities()
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()

  // Compute the range-bar heading text ("Cheaper than similar hotels", etc.)
  // Reuses PriceInsightsBanner's state machine so the wording is consistent
  // with the AP banner. Sheet only opens after data is loaded, so the
  // loading/error flags are fixed to false here.
  const bannerState = showPriceRangeBar
    ? getBannerState(false, true, true, content, false)
    : 'noData'
  const bannerConfig =
    showPriceRangeBar && bannerState !== 'loading' && bannerState !== 'noData'
      ? getBannerConfig(bannerState, content)
      : null
  const rangeBarHeadingText = bannerConfig
    ? formatMessage(
        priceTrendMessages[bannerConfig.messageKey],
        bannerConfig.values
      )
    : ''

  // Range-bar gating: render the block while history is still loading (so we
  // can show a skeleton), or once it loaded with similar-hotels data. Hide it
  // entirely when history has loaded but the hotel has no similar-hotels data
  // (no `latestDetailedPrice`) — nothing meaningful to plot, and the cards
  // above already cover the trend narrative.
  const shouldRenderRangeBarBlock =
    showPriceRangeBar &&
    offerPrice != null &&
    currency != null &&
    (isPriceHistoryLoading || latestDetailedPrice != null)
  const isRangeBarReady =
    shouldRenderRangeBarBlock &&
    !isPriceHistoryLoading &&
    latestDetailedPrice != null

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="s400"
      paddingTop="s300"
      paddingBottom="s500"
    >
      {hotel && (
        <PriceTrendHotelCard
          hotelName={hotel?.hotelName}
          checkIn={checkIn}
          checkOut={checkOut}
          imageUrl={hotel.imageURIs?.[0]}
          rating={hotel?.starRating}
        />
      )}
      {/* Recommendation summary + similar-hotels + hotel-change info cards
          always render here. The percentile range-bar (below) is additive. */}
      <Box
        display="flex"
        flexDirection="column"
        padding="s400"
        borderColor="border.neutral.secondary"
        borderWidth="bw100"
        borderStyle="solid"
        borderRadius="lg"
        position="relative"
        gap="s300"
      >
        <Text variant="bodyXS" colorPath="content.neutral.secondary">
          <FormattedMessageWrapper
            id="priceInsight"
            defaultMessage="Price insight"
          />
        </Text>
        <Text variant="titleM" colorPath="content.neutral.primary">
          {recommendationLabel}
        </Text>
      </Box>
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap="s400"
      >
        {content.similarHotels && (
          <PriceTrendInfoCard
            content={content.similarHotels}
            infoCardName="price-trend-similar-hotels-panel"
            hideIcon={!isMobile}
            onTooltipClick={onTooltipClick}
          />
        )}
        <PriceTrendInfoCard
          content={content.hotelChange}
          infoCardName="price-trend-hotel-change-panel"
          hideIcon={!isMobile}
          onTooltipClick={onTooltipClick}
        />
      </Box>
      <PriceHistoryChart
        data={priceHistory}
        offerPrice={offerPrice}
        isLoading={isPriceHistoryLoading}
      />
      {shouldRenderRangeBarBlock && (
        <Box
          display="flex"
          flexDirection="column"
          padding="s400"
          borderColor="border.neutral.secondary"
          borderWidth="bw100"
          borderStyle="solid"
          borderRadius="lg"
          gap="s400"
          position="relative"
          data-id="price-trend-detail-range-bar"
        >
          {rangeBarHeadingText && (
            <Box paddingBottom="s400" paddingTop="s200">
              <Text variant="titleM" colorPath="content.neutral.primary">
                {rangeBarHeadingText}
              </Text>
            </Box>
          )}
          {isRangeBarReady ? (
            <PriceRangeBar
              p10={latestDetailedPrice!.p10}
              p35={latestDetailedPrice!.p35}
              p65={latestDetailedPrice!.p65}
              p90={latestDetailedPrice!.p90}
              hotelPrice={offerPrice!}
              currency={currency!}
            />
          ) : (
            <PriceRangeBarSkeleton dataTestId="price-trend-detail-range-bar-skeleton" />
          )}
        </Box>
      )}
      {hotel && retention.hasPriceWatch && (
        <Box>
          <PriceWatchCTAWrapper
            hotel={hotel}
            shouldPulse={false}
            ctaComponent={props => (
              <PriceWatchToggle
                {...props}
                hideIcon={false}
                hideInfoIcon={true}
              />
            )}
            componentName="PriceInsightsToggle"
            searchIsCompleted
            priceTrendsContext={priceTrendsContext}
            hasAlertText={hasAlertText}
          />
        </Box>
      )}
      {isHigherThanSimilar &&
        (betterValueHotels.length > 0 || isBetterValueLoading) && (
          <BetterValueCarousel
            hotels={betterValueHotels}
            locationName={locationName}
            isLoading={isBetterValueLoading}
            onHotelClick={onCarouselHotelClicked}
            onSeeMoreClick={onCarouselSeeMoreClicked}
            onDisplayed={onCarouselDisplayed}
            onScrolled={onCarouselScrolled}
          />
        )}
    </Box>
  )
}
